import type { DecisionCheck } from '@/lib/data'

/**
 * The four non-populated screen states.
 *
 * Verbatim from `docs/design/reference/template.html` lines 160–233.
 *
 * These are DESIGNED states, not fallbacks, and each one refuses a specific
 * temptation:
 *
 *   denied  — shows the path rather than hiding it, names the required role
 *             and surfaces the decision_ref. Law 13: every refusal is
 *             explainable and quotable. A hidden menu item teaches an operator
 *             that the console is broken; a stated refusal teaches them who to
 *             ask.
 *   loading — skeletons, and an explicit line saying no cached figures are
 *             shown. A stale balance behind a fresh-looking screen is how a
 *             support agent quotes a number that has already moved.
 *   error   — shows nothing rather than something stale, and says why. A money
 *             screen may not fall back to an analytics copy (law 12).
 *   empty   — explains why empty is PLAUSIBLE here. An operator who cannot
 *             tell an empty queue from a broken screen will treat both the
 *             same way, and one of those is an incident.
 */

const PANEL_BUTTON = {
  height: '28px',
  padding: '0 12px',
  border: '1px solid var(--k-line)',
  background: 'transparent',
  color: 'var(--k-ink)',
  font: 'inherit',
  fontSize: '12px',
  cursor: 'pointer',
} as const

const ROW_LABEL = { color: 'var(--k-mut2)' } as const

/** A finding, not a status line. Same weight the error panel gives a failure. */
const ROW_ALARM = { color: 'var(--k-r-fg)', fontWeight: 700 } as const

/**
 * What the decision explorer said when this console resolved the reference
 * above — the second half of #32.
 *
 * Invariant 8 says `Authorize` writes exactly one decision row for every
 * refusal. The panel used to assert that row existed and later, honestly,
 * admitted it had not looked. Now it looks, and the three answers are three
 * different sentences on purpose:
 *
 * - **read** — the row is quotable, and what it says about ITSELF is the part
 *   that changes an operator's answer: a shadow decision did not affect anyone,
 *   and a superseded one must not be quoted as current.
 * - **absent** — the explorer's own handler has no row for this reference. That
 *   is the invariant failing, and it is a finding: nobody can reconstruct this
 *   refusal, now or in six months. It reads as alarming because it is.
 * - **not checked** — the lookup itself did not answer. We learned nothing
 *   about the row, which is emphatically not the same as learning it is gone;
 *   raising a false invariant-8 alarm is how a real one stops being believed.
 *
 * `undefined` means the caller did not wire a lookup. It keeps the shipped
 * wording rather than inventing an answer, so a screen that renders this panel
 * without resolving the reference still says only what it knows.
 */
function RecordedRows({ check }: { check: DecisionCheck | undefined }) {
  if (check === undefined) {
    return (
      <>
        <span style={ROW_LABEL}>Recorded</span>
        <span>Not checked here — quote the reference above to look the decision up</span>
      </>
    )
  }

  switch (check.kind) {
    case 'checking':
      return (
        <>
          <span style={ROW_LABEL}>Recorded</span>
          <span>Reading the decision row back…</span>
        </>
      )

    case 'no_ref':
      return (
        <>
          <span style={ROW_LABEL}>Recorded</span>
          <span>
            Nothing to look up — the refusal carried no reference, so there is no row to resolve
          </span>
        </>
      )

    case 'unverified':
      return (
        <>
          <span style={ROW_LABEL}>Recorded</span>
          <span>
            Could not be checked — {check.reason}. This console did not learn whether the row
            exists, which is not the same as finding it missing.
          </span>
        </>
      )

    case 'absent':
      return (
        <>
          <span style={ROW_LABEL}>Recorded</span>
          <span style={ROW_ALARM}>
            No — the decision explorer has no row for this reference. Authorize writes exactly one
            row for every refusal (invariant 8), so this refusal is unexplainable: nothing can
            reconstruct why you were refused. Raise it before retrying.
          </span>
        </>
      )

    case 'recorded': {
      const { summary } = check
      return (
        <>
          <span style={ROW_LABEL}>Recorded</span>
          <span>Yes — the authz_decisions row was read back for this reference</span>

          <span style={ROW_LABEL}>Outcome</span>
          <span>
            {summary.outcome} · {summary.attribution}
          </span>

          <span style={ROW_LABEL}>Reason</span>
          <span>{summary.reason}</span>

          <span style={ROW_LABEL}>Decided</span>
          <span style={{ fontFamily: 'var(--k-mono)' }}>{summary.at}</span>

          {summary.amount.kind === 'stated' && (
            <>
              <span style={ROW_LABEL}>Amount</span>
              <span>
                {summary.amount.text}{' '}
                <span style={{ color: 'var(--k-mut2)' }}>{summary.amount.currency}</span>
              </span>
            </>
          )}
          {summary.amount.kind === 'unstateable' && (
            <>
              <span style={ROW_LABEL}>Amount</span>
              <span>Not shown — {summary.amount.reason}</span>
            </>
          )}

          {summary.shadow && (
            <>
              <span style={ROW_LABEL}>Shadow</span>
              <span>
                This rule was not enforcing, so the decision did not affect the customer
              </span>
            </>
          )}

          {summary.superseded !== null && (
            <>
              <span style={ROW_LABEL}>Superseded</span>
              <span style={ROW_ALARM}>{summary.superseded}</span>
            </>
          )}

          {summary.snapshotGap !== null && (
            <>
              <span style={ROW_LABEL}>Replayable</span>
              <span style={ROW_ALARM}>No — {summary.snapshotGap}</span>
            </>
          )}
        </>
      )
    }
  }
}

/**
 * `live` marks a REAL refusal, and its presence is what suppresses the parts of
 * this panel the console cannot actually know.
 *
 * Everything here except the `decision_ref` used to be corpus-derived and read
 * as fact on a genuine 403 (#32). Three separate claims, each wrong in its own
 * way:
 *
 * - **The acting role** came from the masthead's role-PREVIEW dropdown. The real
 *   session is `unknown` while `/v1/admin/me` does not exist (go-wallet#284), so
 *   the panel named a role nobody had resolved.
 * - **The required roles** came from `screen.roles ?? ROLES` — and on a screen
 *   declaring `roles: null` that is the list of every role that exists,
 *   rendered as though it were this screen's requirement. It told the operator
 *   nothing while looking like it told them something. It is now READ, from
 *   `required_role` on the refusal itself, and only reported as unstated when
 *   the refusal genuinely carried none.
 * - **"Recorded: Yes"** was a constant. Invariant 8 says `Authorize` writes
 *   exactly one decision row; a console that ASSERTS it did has turned an
 *   invariant into decoration, and if the row were ever missing this panel
 *   would still have said it was there. It is now READ — see `RecordedRows`
 *   and `lib/data/decision`.
 *
 * The rule matches `ErrorView` directly above: a preview keeps the drawing in
 * full, a real refusal states only what it was told.
 */
export function DeniedView({
  role,
  requiredRoles,
  statedRole,
  previewedRole = null,
  decisionRef,
  live = false,
  check,
}: {
  role: string
  requiredRoles: string
  /**
   * The role being PREVIEWED, when the preview is what caused this refusal.
   *
   * `null` when the operator's own session lacks the scope, which is the
   * ordinary case and keeps the escalation advice. The distinction is the same
   * one #47 was about: an instruction to request elevation is wrong advice for
   * somebody who already holds the role and changed a dropdown, and it sends
   * them to an Admin who has nothing to do.
   */
  previewedRole?: string | null
  /**
   * `required_role` off the refusal, verbatim — `"ops"`, `"finance or growth"`.
   *
   * `null` when the refusal carried none, which is a real state and not a
   * default: an authz refusal is about risk rather than role scope, and the
   * backend deliberately does not fabricate a role for it. Never confused with
   * `requiredRoles`, which is the SCREEN's declared scope and is a drawing.
   */
  statedRole?: string | null
  decisionRef: string
  /** True only for a refusal the backend actually sent. */
  live?: boolean
  /**
   * What resolving `decisionRef` found. Only ever supplied for a real refusal:
   * a preview looks nothing up, so there is nothing for it to report.
   */
  check?: DecisionCheck | undefined
}) {
  return (
    <div
      style={{
        maxWidth: '640px',
        border: '1px solid var(--k-line)',
        borderTop: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
        padding: '20px',
      }}
    >
      <div
        style={{
          fontSize: '10.5px',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--k-mut2)',
        }}
      >
        Authorization
      </div>
      {/* Derived, never fixed. `statedRole` is `required_role` off the refusal
          and is null when the refusal was not about role scope — which the
          panel below already says, in as many words. A fixed heading asserting
          a role problem over that line makes the screen contradict itself two
          lines apart, and sends an operator to request elevation for a refusal
          elevation cannot fix. The most common such refusal today is a signed-in
          session with no staff record: the masthead names it correctly and this
          panel used to talk over it. */}
      <h3 style={{ margin: '6px 0 8px', fontSize: '19px' }}>
        {!live || statedRole != null
          ? 'This screen needs a role you are not holding'
          : 'The backend refused this read, and did not say your role was why'}
      </h3>
      <p style={{ color: 'var(--k-mut)', fontSize: '12.5px', margin: '0 0 14px' }}>
        {live ? (
          <>
            The backend refused this read. This console cannot yet name the role your session
            holds — it has no way to ask (go-wallet#284) — so it will not guess at one.{' '}
            {/* Sending an operator to "quote the reference below" when the line
                below says there is no reference wastes the escalation they were
                just told to make. What to quote depends on what the refusal
                actually carried. */}
            {/* What to DO depends on what the refusal was about, not only on
                whether it carried a reference. Telling somebody to request
                elevation is a wrong instruction when the refusal never
                mentioned a role: the commonest case is a session with no staff
                record at all, and no Admin can elevate a row that does not
                exist. */}
            {statedRole == null
              ? (check?.kind === 'no_ref'
                  ? 'It named no role and carried no decision reference, so there is nothing to quote and nothing to elevate: this is not a scope problem, and the person to reach is whoever administers staff access, not an Admin who can grant a role.'
                  : 'It named no role, so this is not a scope problem — elevation would not change it. Quote the reference below to whoever administers staff access.')
              : (check?.kind === 'no_ref'
                  ? 'This refusal carried no decision reference, so there is nothing to quote: escalate with the required role below — request elevation from an Admin, or hand the queue over.'
                  : 'The reference below is what to quote when you escalate: request elevation from an Admin, or hand the queue over.')}
          </>
        ) : (
          previewedRole !== null ? (
            <>
              You are previewing <strong style={{ color: 'var(--k-ink)' }}>{previewedRole}</strong>,
              and this surface is scoped to {requiredRoles}. Your own session is not what refused
              this — switch the acting role back to see it. A preview can only ever show less than
              your session holds.
            </>
          ) : (
            <>
              Your session acts as <strong style={{ color: 'var(--k-ink)' }}>{role}</strong>. This
              surface is scoped to {requiredRoles}. The path is shown rather than hidden so you can
              escalate — request elevation from an Admin, or hand the queue over.
            </>
          )
        )}
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '6px 14px',
          fontSize: '12px',
          padding: '10px',
          background: 'var(--k-bg)',
          border: '1px solid var(--k-line)',
        }}
      >
        <span style={{ color: 'var(--k-mut2)' }}>Required role</span>
        {/* A live panel names what the REFUSAL named and nothing else. The
            backend sends `required_role` on every RBAC denial precisely so the
            console does not have to hold a second copy of the permission model
            — and the screen's own `roles` list is a drawing, which on a screen
            declaring `roles: null` is every role that exists, reading as a
            requirement while being none. When the refusal carried no role at
            all (an authz refusal is about risk, not scope) that absence is
            stated rather than filled in. */}
        <span style={{ fontWeight: 600 }}>
          {live
            ? (statedRole ?? 'not stated by the backend — this refusal was not about role scope')
            : requiredRoles}
        </span>
        <span style={{ color: 'var(--k-mut2)' }}>decision_ref</span>
        <span style={{ fontFamily: 'var(--k-mono)' }}>{decisionRef}</span>
        {/* A preview keeps the drawing's assertion, which is the copy under
            review and describes a refusal that did not happen. A real refusal
            reports the lookup instead — invariant 8 guarantees the row, and the
            only time anyone reads this panel closely is the time the guarantee
            failed. */}
        {live ? (
          <RecordedRows check={check} />
        ) : (
          <>
            <span style={{ color: 'var(--k-mut2)' }}>Recorded</span>
            <span>Yes — authz_decisions row written for this attempt</span>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
        <button type="button" className="k-hov" style={PANEL_BUTTON}>
          Copy decision_ref
        </button>
        <button type="button" className="k-hov" style={PANEL_BUTTON}>
          Request elevation
        </button>
      </div>
    </div>
  )
}

const SKEL_CARDS = [1, 2, 3, 4]
const SKEL_ROWS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

export function LoadingView() {
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4,1fr)',
          gap: '1px',
          background: 'var(--k-line)',
          border: '1px solid var(--k-line)',
          marginBottom: '14px',
        }}
      >
        {SKEL_CARDS.map((n) => (
          <div key={n} style={{ background: 'var(--k-panel)', padding: '12px' }}>
            <div data-sk="1" style={{ height: '9px', width: '52%' }} />
            <div data-sk="1" style={{ height: '22px', width: '34%', marginTop: '10px' }} />
            <div data-sk="1" style={{ height: '9px', width: '78%', marginTop: '10px' }} />
          </div>
        ))}
      </div>
      <div style={{ border: '1px solid var(--k-line)', background: 'var(--k-panel)' }}>
        <div
          style={{
            height: '30px',
            borderBottom: '2px solid var(--k-rule)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px',
            gap: '14px',
          }}
        >
          <div data-sk="1" style={{ height: '8px', width: '120px' }} />
          <div data-sk="1" style={{ height: '8px', width: '80px' }} />
        </div>
        {SKEL_ROWS.map((n) => (
          <div
            key={n}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '18px',
              height: '34px',
              padding: '0 10px',
              borderBottom: '1px solid var(--k-line)',
            }}
          >
            <div data-sk="1" style={{ height: '8px', width: '70px' }} />
            <div data-sk="1" style={{ height: '8px', width: '150px' }} />
            <div data-sk="1" style={{ height: '8px', width: '90px' }} />
            <div data-sk="1" style={{ height: '8px', width: '60px', marginLeft: 'auto' }} />
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: '10px',
          color: 'var(--k-mut2)',
          fontSize: '11.5px',
          fontFamily: 'var(--k-mono)',
        }}
      >
        reading postgres:ledger · no cached figures shown while loading
      </div>
    </div>
  )
}

const ERROR_BODY = `ledger.read timeout after 8000ms
  at balances.snapshot(customer_id=1042)
  provider_balance_snapshots: last row 2026-07-31 13:58 UTC (4m old)
  retry is safe — this read is idempotent`

/**
 * `detail` is the account of a REAL failed read, and its presence is what tells
 * this view it is not a preview.
 *
 * The copy above is a worked example — a named timeout, customer 1042, a
 * provider snapshot four minutes old, a trace id. On the 114 corpus screens
 * that is the design and it is fine. Over a live screen's actual failure it is
 * a fabrication in the one place an operator goes to find out what broke: they
 * would open customer 1042 and chase a ledger timeout that never happened. So a
 * live failure states what actually failed and the example is suppressed —
 * including the trace id, which this console is not given.
 */
export function ErrorView({ onRetry, detail }: { onRetry: () => void; detail?: string }) {
  const live = detail !== undefined
  return (
    <div
      style={{
        maxWidth: '700px',
        border: '1px solid var(--k-r-fg)',
        borderTop: '2px solid var(--k-r-fg)',
        background: 'var(--k-panel)',
        padding: '18px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          data-tone="r"
          style={{
            padding: '1px 6px',
            fontSize: '9.5px',
            fontWeight: 800,
            letterSpacing: '.07em',
            textTransform: 'uppercase',
          }}
        >
          Query failed
        </span>
        <span
          style={{ fontFamily: 'var(--k-mono)', fontSize: '11.5px', color: 'var(--k-mut)' }}
        >
          {live ? 'no trace id — this console is not given one yet' : 'trace 4b1e-…-90fa'}
        </span>
      </div>
      <h3 style={{ margin: '10px 0 6px', fontSize: '18px' }}>
        This screen could not read its authoritative source
      </h3>
      <p
        style={{
          color: 'var(--k-mut)',
          fontSize: '12.5px',
          margin: '0 0 12px',
          maxWidth: '70ch',
        }}
      >
        {live
          ? 'The read did not complete, so this screen knows nothing. Nothing is shown rather than something stale — a money screen may not fall back to an analytics copy (law 12), and it may not fall back to the design corpus either. Retry, or open the platform status screen.'
          : 'The ledger read timed out after 8000 ms. Nothing is shown rather than something stale — a money screen may not fall back to an analytics copy (law 12). Retry, or open the platform status screen.'}
      </p>
      <div
        style={{
          fontFamily: 'var(--k-mono)',
          fontSize: '11.5px',
          background: 'var(--k-bg)',
          border: '1px solid var(--k-line)',
          padding: '10px',
          whiteSpace: 'pre-wrap',
          color: 'var(--k-mut)',
        }}
      >
        {detail ?? ERROR_BODY}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
        <button
          type="button"
          onClick={onRetry}
          style={{
            height: '28px',
            padding: '0 12px',
            border: 0,
            background: 'var(--k-accent)',
            color: 'var(--k-accentink)',
            font: 'inherit',
            fontWeight: 800,
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          Retry read
        </button>
        <button type="button" className="k-hov" style={PANEL_BUTTON}>
          Open worker health
        </button>
      </div>
    </div>
  )
}

/**
 * The backend answered and had nothing.
 *
 * # It no longer takes the screen, and that is the fix
 *
 * It used to read `screen.empty` — a `[title, body]` pair written into the
 * design corpus per screen — and render it over a live read that returned zero
 * rows. Those pairs are narratives, not statements: `cust-360`'s said
 * *"Accepted by the EMI 3 hours ago, no inbound credit has arrived. The next
 * expected event is the first payer credit — the payout-guidance content for
 * their platform has been sent."* Every clause of that is a specific claim
 * about a specific customer, and it drew for a customer id that resolves to
 * nothing.
 *
 * An empty read supports exactly one sentence: the backend answered and there
 * were no rows. Anything beyond it is invention, and inventing it here is worse
 * than inventing a table row because it arrives in the one place an operator
 * looks to find out why a screen is blank.
 *
 * A loader that genuinely knows WHY its queue is empty says so in a note block
 * it composes from the read — which several already do, and which is the right
 * home for it, because the loader has the payload and this component has only
 * a screen id.
 */
export function EmptyView({ clock }: { clock: string }) {

  return (
    <div
      style={{
        maxWidth: '620px',
        border: '1px solid var(--k-line)',
        borderTop: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
        padding: '20px',
      }}
    >
      <div
        style={{
          fontSize: '10.5px',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--k-mut2)',
        }}
      >
        Nothing here
      </div>
      <h3 style={{ margin: '6px 0 8px', fontSize: '19px' }}>The read returned no rows</h3>
      <p
        style={{
          color: 'var(--k-mut)',
          fontSize: '12.5px',
          margin: 0,
          maxWidth: '66ch',
          textWrap: 'pretty',
        }}
      >
        This is an answer, not a failure — the backend was asked and had nothing to send.
      </p>
      <div style={{ height: '2px', background: 'var(--k-line)', margin: '16px 0' }} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '5px 14px',
          fontSize: '11.5px',
          color: 'var(--k-mut)',
        }}
      >
        <span style={{ color: 'var(--k-mut2)' }}>Checked</span>
        <span style={{ fontFamily: 'var(--k-mono)' }}>{clock}</span>
      </div>
    </div>
  )
}
